// @ts-check
import { useContext, useState } from 'react'
import SocialButtons from './SocialButtons'
import TextInput from './TextInput'
import { HOSTNAME } from '../../mdc_modules/mdcApiFetch'
import { AuthContext } from '../../providers/AuthContextProvider'
import InlineLoader from '../InlineLoader'
import EyeIcon from '../../icons/EyeIcon'
import { Tos } from './Tos'

/** @typedef {{ email?: string, onForgotPassword?: () => void, onSuccess?: () => void }} Props */
/** @param {Props} props */
export default function Login({ email = '', onForgotPassword, onSuccess }) {
  const { reload } = useContext(AuthContext)
  const [formData, setFormData] = useState(
    /** @type {{ email: string, password: string }} */ ({ email: email, password: '' })
  )
  const [errors, setErrors] = useState(
    /** @type {Record<string, string | null | undefined>} */ ({ email: null, password: null })
  )
  const [loading, setLoading] = useState(false)

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }))
    setErrors((prev) => ({ ...prev, [event.target.name]: null, submissionError: null }))
  }

  /**
   * @param {React.FormEvent<HTMLFormElement>} event
   */
  const handleSubmit = async (event) => {
    setLoading(true)
    event.preventDefault()
    try {
      let hasErrors = false
      setErrors({})
      if (!formData.email) {
        setErrors((prev) => ({ ...prev, email: 'Email is required' }))
        hasErrors = true
      }
      if (!formData.password) {
        setErrors((prev) => ({ ...prev, password: 'Password is required' }))
        hasErrors = true
      }
      if (hasErrors) {
        return
      }

      // Trim all form data before submission
      const trimmedFormData = {
        action: 'login',
        email: formData['email'].trim(),
        password: formData['password'].trim(),
      }

      const response = await fetch(`${HOSTNAME}/auth/login`, {
        method: 'POST',
        body: JSON.stringify(trimmedFormData),
      })
      if (response.ok) {
        const { success, message, action } = await response.json()
        if (success) {
          await reload()
          onSuccess?.()
        } else {
          if (action === 'maxSessionsLogin') {
            window.location.href = `${HOSTNAME}/account/email-verification.html`
          } else {
            setErrors({ submissionError: message })
          }
        }
      } else {
        setErrors({
          submissionError: 'Error submitting request. Please try again or contact support.',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  /**
   * @param {React.MouseEvent<HTMLAnchorElement>} event
   */
  const handelForgotPasswordClick = (event) => {
    event.preventDefault()
    onForgotPassword?.()
  }

  return (
    <>
      <form className="sb-sign-in-pop-up__form-layout" onSubmit={handleSubmit}>
        <TextInput
          name="email"
          label="Email"
          type="email"
          maxLength={50}
          value={formData.email}
          error={errors.email}
          onChange={handleChange}
        />

        <TextInput
          name="password"
          label="Password"
          type="password"
          maxLength={50}
          value={formData.password}
          error={errors.password}
          leftIcon={<EyeIcon />}
          onChange={handleChange}
        />

        <label
          style={{
            textAlign: 'right',
            textTransform: 'uppercase',
            fontSize: '11px',
            lineHeight: '11px',
            letterSpacing: '0.04rem',
            textDecoration: 'underline',
            padding: '8px 0',
          }}
        >
          <a onClick={handelForgotPasswordClick}>Forgot Password?</a>
        </label>

        <button type="submit" className="sb-button sb-button--primary">
          {loading ? <InlineLoader /> : 'Sign In'}
        </button>

        <div
          style={{
            color: '#BB2A33',
            textAlign: 'center',
            fontSize: '12px',
            lineHeight: '15.6px',
            transition: 'height .5s',
            height: errors.submissionError ? 'auto' : 0,
          }}
        >
          {errors.submissionError}
        </div>
      </form>
      <SocialButtons />
      <Tos />
    </>
  )
}
